import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { withProcedureWraper } from "../../..";
import { getEnv } from "../../../../../AppConfigs";
import { Routes } from "../../../../../AppRoutes";
import { Icon } from "../../../../../components/icon";
import { approverType } from "../../../../../constants/itCodeRequestConstants";
import { TravelCardRequestService } from "../../../../../services/company-office/TravelCardRequest";
import { useSelector } from "../../../../../store/store";
import { ProcessContextProvider, ProcessContextType } from "../../../process-context";
import VehicleTravelCardInformation from "./information";
import VehicleTravelCardProgress from "./progress";
import { useQueryClient } from "@tanstack/react-query";

export type ExtraStateContext = {
  isLongTerm: boolean;

  isProvide: boolean;
  isRenew: boolean;
  isEvict: boolean;
  pickedItHead: any;
  onApproverChange: (selectedOptions: any) => void;
};

export const siteUrl = getEnv("REACT_APP_APPROVER_BUSINESS_VEHICLE_CARD_REQUEST_URL");

export const detailBusinessVeCardPage: FC = withProcedureWraper(() => {
  /**
   * Selectors
   */
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.user);
  const currentUserName = user?.current?.username;

  const history = useHistory();
  const { id } = useRouteMatch().params as any;

  /**
   * States
   */
  const [vehicleTravelCardRequest, setVehicleTravelCardRequest] = useState(null as any);
  const [comment, setComment] = useState("");
  const [approvalType, setApprovalType] = useState(0);
  const [approvalStep, setApprovalStep] = useState(approverType.approver);
  const [pickedItHead, setPickedItHead] = useState(null as any);

  const checkApproverType = useCallback(() => {
    const {
      eApprovedBy,
      eApprovedByITLead,
      eApprovedByITHead,
      eAssignee,
      eApprovedStatus,
      eApprovedByITLeadStatus,
      eApprovedByITHeadStatus,
      eAssigneeStatus,
    } = vehicleTravelCardRequest.fields;
    const approverEmail = eApprovedBy ? eApprovedBy.split("|")[0] : "";
    const approvedByITLeadEmail = eApprovedByITLead ? eApprovedByITLead.split("|")[0] : "";
    const approvedByITHeadEmail = eApprovedByITHead ? eApprovedByITHead.split("|")[0] : "";
    const assigneeEmail = eAssignee ? eAssignee.split("|")[0] : "";

    if (currentUserName.toLowerCase() === approverEmail.toLowerCase() && !eApprovedStatus) {
      setApprovalType(approverType.approver);
      return;
    }
    if (currentUserName.toLowerCase() === approvedByITLeadEmail.toLowerCase() && !eApprovedByITLeadStatus) {
      setApprovalType(approverType.itlead);
      return;
    }
    if (currentUserName.toLowerCase() === approvedByITHeadEmail.toLowerCase() && !eApprovedByITHeadStatus) {
      setApprovalType(approverType.ithead);
      return;
    }
    if (currentUserName.toLowerCase() === assigneeEmail.toLowerCase() && !eAssigneeStatus) {
      setApprovalType(approverType.assignee);
      return;
    }
    setApprovalType(0);
  }, [currentUserName, vehicleTravelCardRequest?.fields]);

  const checkApproverStep = useCallback(() => {
    const { eApprovedStatus, eApprovedByITLeadStatus, eApprovedByITHeadStatus, eAssigneeStatus } =
      vehicleTravelCardRequest.fields;
    if (!eApprovedStatus) {
      setApprovalStep(approverType.approver);
      return;
    }
    if (!eApprovedByITLeadStatus) {
      setApprovalStep(approverType.itlead);
      return;
    }
    if (!eApprovedByITHeadStatus) {
      setApprovalStep(approverType.ithead);
      return;
    }
    if (!eAssigneeStatus) {
      setApprovalStep(approverType.assignee);
      return;
    }
  }, [vehicleTravelCardRequest?.fields]);

  const onApproverChange = useCallback((selectedOptions: any) => {
    setPickedItHead(selectedOptions ? selectedOptions.value : "");
  }, []);

  const handleUpdatePin = useCallback(
    async (isPin: boolean) => {
      try {
        if (!vehicleTravelCardRequest) return;
        const pins: any[] = vehicleTravelCardRequest.fields.ePins
          ? JSON.parse(vehicleTravelCardRequest.fields.ePins)
          : [];
        const index = pins.findIndex((pin: string) => pin === currentUserName?.toLowerCase());
        if (!isPin && index < 0) return;
        if (isPin && index > -1) return;
        if (!isPin && index > -1) pins.splice(index, 1);
        if (isPin && index < 0) pins.push(currentUserName?.toLowerCase());

        const fields = {
          ePins: JSON.stringify(pins),
        };

        await TravelCardRequestService.updateTravelCardRequest(id, fields);
        queryClient.invalidateQueries({ queryKey: ["vehicle-travel-card-requests"] });
      } catch (error) {
        console.log(error);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [currentUserName, id, vehicleTravelCardRequest],
  );

  const isPin = useCallback(() => {
    if (!vehicleTravelCardRequest) return false;
    const pins: any[] = vehicleTravelCardRequest.fields.ePins ? JSON.parse(vehicleTravelCardRequest.fields.ePins) : [];
    return pins.findIndex((pin: string) => pin === currentUserName?.toLowerCase()) >= 0;
  }, [currentUserName, vehicleTravelCardRequest]);

  useEffect(() => {
    TravelCardRequestService.getTravelCardRequest(id)
      .then((res) => {
        setVehicleTravelCardRequest(res);
        // if (res.fields) {
        //   setComment(res.fields.eApprovalComments);
        // }
      })
      .catch((err) => console.log(err.message));
  }, [id]);

  useEffect(() => {
    if (vehicleTravelCardRequest) {
      checkApproverType();
      checkApproverStep();
    }
  }, [checkApproverStep, checkApproverType, vehicleTravelCardRequest]);

  const ctx = useMemo<ProcessContextType<ExtraStateContext>>(
    () => ({
      Request: vehicleTravelCardRequest,
      approvalStep,
      approvalType,
      comment,
      setComment,

      /**
       * Extra props
       */
      isLongTerm: vehicleTravelCardRequest?.fields.eStage === "longTerm",
      isProvide: vehicleTravelCardRequest?.fields.eSuggestion === "provide",
      isEvict: vehicleTravelCardRequest?.fields.eSuggestion === "evict",
      isRenew: vehicleTravelCardRequest?.fields.eSuggestion === "renew",
      pickedItHead,
      onApproverChange,
      updatePin: handleUpdatePin,
      isPin: isPin,
    }),
    [
      vehicleTravelCardRequest,
      approvalStep,
      approvalType,
      comment,
      pickedItHead,
      onApproverChange,
      handleUpdatePin,
      isPin,
    ],
  );

  const handleGoBack = () => {
    if (history.action === "PUSH") history.goBack();
    else history.push(Routes.businessVehicleCardPage.path);
  };

  return (
    <div className="ItCodeGrantedDetailPage pd15">
      <div className="PageHead">
        <div className="title">
          <div className="btnBack" onClick={handleGoBack}>
            <Icon.ArrowLeft />
          </div>
          {"Xem Xét Yêu Cầu Duyệt"}
        </div>
      </div>

      <ProcessContextProvider context={ctx}>
        <VehicleTravelCardProgress />
        <VehicleTravelCardInformation />
      </ProcessContextProvider>
    </div>
  );
});
